import { useState } from 'react';
import { useAuth , useTheme } from '@repo/shared';
import './LoginPage.css';
import './PasswordChallengePage.css';

export default function PasswordChallengePage({ onSuccess, onCancel, challengeData }) {
  const { completePasswordChallenge } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('weak');

  const { appTheme } = useTheme();

  // Password strength validator
  const checkPasswordStrength = (pwd) => {
    let strength = 'weak';
    
    if (pwd.length >= 8) {
      let hasUppercase = /[A-Z]/.test(pwd);
      let hasLowercase = /[a-z]/.test(pwd);
      let hasNumber = /[0-9]/.test(pwd);
      let hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
      
      let criteria = [hasUppercase, hasLowercase, hasNumber, hasSpecial].filter(Boolean).length;
      
      if (criteria >= 3) strength = 'strong';
      else if (criteria >= 2) strength = 'medium';
    }
    
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    checkPasswordStrength(pwd);
  };

  const validatePasswords = () => {
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required');
      return false;
    }

    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters long');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    // Check password requirements
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      setError(
        'Password must contain uppercase, lowercase, number, and special character'
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);

    try {
      const result = await completePasswordChallenge(
        challengeData.username,
        newPassword,
        challengeData.session,
      );

      // Success - notify parent
      onSuccess('LOGIN_SUCCESS', result);
    } catch (err) {
      let errorMessage = 'Failed to set password';

      if (err.message.includes('password does not conform to policy')) {
        errorMessage = 'Password does not meet complexity requirements';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error('Password challenge error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <img 
            alt={appTheme?.appName || ''} 
            src={new URL(`../../images/${appTheme?.logo || 'logo-default.png'}`, import.meta.url).href}
            className="login-logo-img"
          />
        </div>
        
        <h1>Set Your New Password</h1>
        <p className="login-subtitle">First-time login setup</p>
        
        <p className="instruction-text">
          This is your first login. Please set a new permanent password before continuing.
        </p>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={handlePasswordChange}
              disabled={loading}
              required
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            {newPassword && (
              <div className={`password-strength ${passwordStrength}`}>
                Strength: <strong>{passwordStrength}</strong>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
              placeholder="Confirm password"
              autoComplete="new-password"
            />
          </div>

          <div className="password-requirements">
            <p>Password Requirements:</p>
            <ul>
              <li>At least 12 characters</li>
              <li>Uppercase letter (A-Z)</li>
              <li>Lowercase letter (a-z)</li>
              <li>Number (0-9)</li>
              <li>Special character (!@#$%^&*)</li>
            </ul>
          </div>

          <div className="button-group">
            <button 
              type="submit" 
              disabled={loading}
              className="submit-button"
            >
              {loading ? 'Setting password...' : 'Set Password'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
